// Тоталите на A4 хартията на фактурата (словом + сумите). Изнесени от
// `InvoicePreviewModal` при добавянето на бакшиша (Tailwind; хартията е
// винаги светла → литерални цветове).
//
// Тоталите идват от същата разбивка като редовете → реконсилират се винаги
// (вкл. за стари фактури). Брутото (Обща стойност) е инвариант = Σ редове;
// може само нето/ДДС да се преразпредели с цент спрямо първоначално
// издаденото. Бакшишът без ДДС (група А) е извън „Всичко сума" и ДДС — отделен
// ред „Бакшиш (без ДДС)" преди „Обща стойност", само когато го има; облагаемият
// бакшиш (група Б/В) е в ДДС групата си и не е в този ред.

import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { moneyToWordsBg } from "../../../../../lib/bgNumberToWords";
import type { VatBreakdown } from "../../../../../lib/vat";

const formatMoney = (value: number) =>
  Number.isFinite(value) ? value.toFixed(2) : "0.00";

const SumRow = ({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) => (
  <div
    className={`flex justify-between gap-3 pb-[2px] ${
      emphasis
        ? "mt-1 border-t border-[#C9C7BF] pt-[6px] text-[14px] font-bold"
        : "pt-[2px] text-[12.5px] font-medium"
    }`}
  >
    <span className={emphasis ? "text-[#241F1A]" : "text-[#5C5A52]"}>{label}:</span>
    <span className="font-pos-mono tabular-nums">{value}</span>
  </div>
);

type Props = {
  breakdown: VatBreakdown;
  isNarrow: boolean;
};

export const InvoicePaperTotals = ({ breakdown, isNarrow }: Props) => {
  const { t } = useTranslation();
  const vatGroups = breakdown.groups.filter((group) => group.rate > 0);

  return (
    <div
      className={`mb-[18px] grid gap-[14px] ${
        isNarrow ? "grid-cols-[1fr]" : "grid-cols-[1fr_240px]"
      }`}
    >
      <div>
        <div className="mb-1 text-[11px] text-[#5E5749]">
          {t("invoicePreview.totalWords")}:
        </div>
        <div className="text-[13.5px] font-bold">
          {moneyToWordsBg(breakdown.grandTotal)}
        </div>
      </div>
      <div className="flex flex-col gap-[3px]">
        <SumRow
          label={t("invoicePreview.sumTotal")}
          value={formatMoney(breakdown.totalSum)}
        />
        {breakdown.nonTaxable > 0 && (
          <SumRow
            label={t("invoicePreview.sumNonTaxable")}
            value={formatMoney(breakdown.nonTaxable)}
          />
        )}
        {vatGroups.map((group) => (
          <Fragment key={`vat-group-${group.rate}`}>
            <SumRow
              label={`${t("invoicePreview.sumTaxable")} (${group.rate}%)`}
              value={formatMoney(group.net)}
            />
            <SumRow
              label={`${t("invoicePreview.sumVat")} (${group.rate}%)`}
              value={formatMoney(group.vat)}
            />
          </Fragment>
        ))}
        {breakdown.tipTotal > 0 && (
          <SumRow
            label={t("invoiceTips.totalTip")}
            value={formatMoney(breakdown.tipTotal)}
          />
        )}
        <SumRow
          label={t("invoicePreview.sumGrand")}
          value={formatMoney(breakdown.grandTotal)}
          emphasis
        />
      </div>
    </div>
  );
};
